/**
 * addFields do passbook no Operador (espelha dash partials/addFields.ejs + validate.js ~366).
 *
 * Slugs especiais NÃO renderizam input extra: moneyValorCompra e numberCodCupom usam
 * os campos nativos Valor da compra / Código da Compra.
 */

import type { Passbook, PassbookAddField } from '@/shared/types';

export const SPECIAL_ADD_FIELD_SLUGS = new Set(['moneyValorCompra', 'numberCodCupom']);

const SUPPORTED_TYPES = new Set(['text', 'money', 'number', 'select', 'radio', 'textarea']);

function isDisplayed(field: PassbookAddField): boolean {
  const d = field.display;
  if (d === false || d === 'false') return false;
  return true;
}

/** Campos visíveis no Operador (sem store / slugs especiais). */
export function visibleAddFields(passbook: Passbook): PassbookAddField[] {
  if (!passbook.showAddFields && !(passbook.addFields?.length)) return [];
  const list = passbook.addFields ?? [];
  return list.filter((f) => {
    if (!f?.slug || SPECIAL_ADD_FIELD_SLUGS.has(f.slug)) return false;
    if (!isDisplayed(f)) return false;
    const t = (f.type ?? 'text').toLowerCase();
    if (t === 'store') return false;
    return SUPPORTED_TYPES.has(t) || !f.type;
  });
}

export function findAddField(passbook: Passbook, slug: string): PassbookAddField | undefined {
  return (passbook.addFields ?? []).find((f) => f.slug === slug);
}

/** numberCodCupom required no passbook → Código da Compra obrigatório. */
export function purchaseCodeFieldMeta(passbook: Passbook): {
  label: string;
  required: boolean;
} {
  const field = findAddField(passbook, 'numberCodCupom');
  return {
    label: field?.label?.trim() || 'Código da Compra',
    required: Boolean(field?.required),
  };
}

/** Label/required do valor da compra — espelha addFields.moneyValorCompra (legado). */
export function purchaseValueFieldMeta(passbook: Passbook): {
  label: string;
  required: boolean;
} {
  const field = findAddField(passbook, 'moneyValorCompra');
  return {
    label: field?.label?.trim() || 'Valor da compra',
    required: Boolean(field?.required),
  };
}

export function parseAddFieldOptions(options: PassbookAddField['options']): string[] {
  if (!options) return [];
  if (Array.isArray(options)) {
    return options.map((o) => String(o).trim()).filter(Boolean);
  }
  return String(options)
    .split(/[;|,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function getAddFieldErrors(
  passbook: Passbook,
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of visibleAddFields(passbook)) {
    if (!field.required) continue;
    const v = (values[field.slug] ?? '').trim();
    if (!v) errors[field.slug] = `${field.label || field.slug} é obrigatório.`;
  }
  const cupom = findAddField(passbook, 'numberCodCupom');
  if (cupom?.required) {
    const code = (values.numberCodCupom ?? '').trim();
    if (!code) errors.numberCodCupom = `${cupom.label || 'Código da Compra'} é obrigatório.`;
  }
  return errors;
}

/**
 * Monta `addFields` JSON para o body legado (validate.js ~366).
 * Ordem: extras → numberCodCupom → NFC-e → moneyValorCompra **sempre por último**.
 */
export function buildOperatorAddFields(opts: {
  values?: Record<string, string>;
  nfceAccessKey?: string;
  nfceRawUrl?: string;
}): string | undefined {
  const raw = opts.values ?? {};
  const extras: Record<string, string> = {};
  let numberCodCupom: string | undefined;
  let moneyValorCompra: string | undefined;

  for (const [k, v] of Object.entries(raw)) {
    const trimmed = String(v ?? '').trim();
    if (!trimmed) continue;
    if (k === 'numberCodCupom') {
      numberCodCupom = trimmed;
      continue;
    }
    if (k === 'moneyValorCompra') {
      moneyValorCompra = trimmed;
      continue;
    }
    extras[k] = trimmed;
  }

  const add: Record<string, string> = { ...extras };
  if (numberCodCupom) add.numberCodCupom = numberCodCupom;
  if (opts.nfceAccessKey) add.chNFe = opts.nfceAccessKey;
  if (opts.nfceRawUrl) add.nfceUrl = opts.nfceRawUrl;
  if (moneyValorCompra) add.moneyValorCompra = moneyValorCompra;

  return Object.keys(add).length ? JSON.stringify(add) : undefined;
}
